/**
 * CheTuo Web — Orders page (工单管理).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { listOrders, type OrderListItem } from '../../api/orders';

const STATUS_TABS = [
  { label: '全部', value: '' },
  { label: '待处理', value: 'open' },
  { label: '施工中', value: 'in_progress' },
  { label: '待收款', value: 'done' },
  { label: '已收款', value: 'paid' },
];

const STATUS_LABELS: Record<string, string> = {
  open: '待处理',
  in_progress: '施工中',
  done: '待收款',
  paid: '已收款',
  unpaid: '挂账',
  cancelled: '已取消',
};

const STATUS_VARIANTS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  open: 'default',
  in_progress: 'primary',
  done: 'warning',
  paid: 'success',
  unpaid: 'danger',
  cancelled: 'default',
};

export function OrdersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', status, search, page],
    queryFn: () => listOrders({ status: status || undefined, q: search || undefined, page, pageSize: 20 }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">工单管理</h1>
        <Button onClick={() => navigate('/shop-admin/orders/new')}>+ 新建工单</Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setStatus(tab.value); setPage(1); }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Input
          placeholder="搜索客户名、车牌或工单号"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="max-w-xs"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>工单列表 ({data?.total ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-8 text-center text-gray-500">加载中...</p>
          ) : !data?.items.length ? (
            <p className="py-8 text-center text-gray-500">暂无工单</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="pb-3 pr-4 font-medium">车牌</th>
                  <th className="pb-3 pr-4 font-medium">客户</th>
                  <th className="pb-3 pr-4 font-medium">状态</th>
                  <th className="pb-3 pr-4 font-medium">金额</th>
                  <th className="pb-3 pr-4 font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((order: OrderListItem) => (
                  <tr
                    key={order.id}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                    onClick={() => navigate(`/shop-admin/orders/${order.id}`)}
                  >
                    <td className="py-3 pr-4 font-medium text-gray-900">{order.vehiclePlate}</td>
                    <td className="py-3 pr-4 text-gray-700">{order.customerName}</td>
                    <td className="py-3 pr-4">
                      <Badge variant={STATUS_VARIANTS[order.status] ?? 'default'}>
                        {STATUS_LABELS[order.status] ?? order.status}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 font-medium text-gray-900">¥{order.totalAmount}</td>
                    <td className="py-3 pr-4 text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString('zh-CN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && data.total > data.pageSize && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                第 {data.page} 页，共 {Math.ceil(data.total / data.pageSize)} 页
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  上一页
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * data.pageSize >= data.total}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
